import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { SearchService } from '@syncfusion/ej2-angular-documenteditor';
import { LocalStorageService } from '../../../../services/local-storage-service/local-storage.service';
import { MetaModelService } from '../../../../services/metamodel/metamodel-provider.service';
import { RestProviderService } from '../../../../services/rest-provider/rest-provider.service';
import { TranslationsProviderService } from '../../../../services/translations-provider/translations-provider.service';
import { UserService } from '../../../../services/user/user-provider.service';
import { SemSearchComponent } from '../../sem-search/sem-search.component';
import { SemVersionDialogComponent } from '../sem-version-dialog/sem-version-dialog.component';
import * as jsondiffpatch from 'jsondiffpatch';
import { parse } from 'path';

@Component({
    selector: 'sem-audit-log',
    templateUrl: './sem-audit-log.component.html',
    styleUrl: './sem-audit-log.component.css',
    standalone: false
})
export class SemAuditLogComponent implements OnInit {

  updates: any[] = [];
  reads: any[] = [];
  customactions: any[] = [];
  inserts: any[] = [];
  currentId = 0;
  selectedItem: any = null;
  oldValues: any = null;
  newValues: any = null;
  differences: any = null;
  openDropdowns: Set<number> = new Set();

  // Pagination properties
  pageSize = 50;
  readsCurrentPage = 1;
  updatesCurrentPage = 1;
  customActionsCurrentPage = 1;

  constructor(
    public dialogRef: MatDialogRef<SemSearchComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    public translationsService: TranslationsProviderService,
    public metaModelService: MetaModelService,
    public userService: UserService,
    public localStorageService: LocalStorageService,
    public restClient: RestProviderService,
    public dialog: MatDialog,
    public activatedRoute: ActivatedRoute) {
  }

  ngOnInit() {

    try {
      this.currentId = parseInt(window.location.href.substring(window.location.href.indexOf("?id=") + 4, window.location.href.length));
      
      this.getAuditLogRecords("read");
      this.getAuditLogRecords("update");
      this.getAuditLogRecords("customaction");
      this.getAuditLogRecords("insert");
    } catch (e) {

    }

    /*this.activatedRoute.queryParams.subscribe(params => {

      if (params != null && typeof params != "undefined") {
        if (params["id"] != null && typeof params["id"] != "undefined") {

          this.currentId = parseInt(params["id"]);
          this.getAuditLogRecords("getupdates");
          this.getAuditLogRecords("getreads");
        }
      }
    });*/
  }

  clean() {
    this.selectedItem = null;
    this.oldValues = null;
    this.newValues = null;
    this.differences = [];
  }

  onTabChanged(event: any) {
    this.clean(); // Clear selection when switching tabs
  }

  setSelected(read) {
    this.selectedItem = read;

    // Check if this is an insert (no OLD_NEW_VALUES)
    if (!read.LogData.includes('OLD_NEW_VALUES =>')) {
      // Handle insert data - extract REQUEST data and filter out null/0 values
      const requestData = this.extractRequestData(read.LogData);
      this.differences = this.filterNonEmptyValues(requestData);
      return;
    }

    const oldNewValuesString = this.extractOldNewValues(read.LogData);

    // Step 2: Extract the 'Old' and 'New' parts from the block
    const oldString = this.extractObject('Old', oldNewValuesString);
    const newString = this.extractObject('New', oldNewValuesString);

    // Step 3: Parse them as JSON-like objects
    const oldNewJson = JSON.parse(oldNewValuesString);
    this.oldValues = oldNewJson.Old;
    this.newValues = oldNewJson.New;

    const diffpatcher = jsondiffpatch.create({
      propertyFilter: (name, context) => {
        // Ignore 'id' and 'timestamp' properties
        if (name === 'EntryDate' || name === 'Version' || name === 'Institution' || name === 'LastChangeDate' || name === 'SerializationObjectFormLink' || name === 'ClientId') {
          return false;
        }
        return true; // Keep all other properties
      }
    });
    this.differences = diffpatcher.diff(this.oldValues, this.newValues);
  }

  extractRequestData(logData: string): any {
    try {
      const requestStart = logData.indexOf('REQUEST =>') + 10;
      const requestDataString = logData.substring(requestStart).trim();
      return JSON.parse(requestDataString);
    } catch (error) {
      console.log('Error parsing request data:', error);
      return {};
    }
  }

  filterNonEmptyValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      const filtered = obj.filter(item => item !== null && item !== 0 && item !== '').map(item => this.filterNonEmptyValues(item));
      return filtered.length > 0 ? filtered : null;
    }
    
    if (typeof obj === 'object') {
      const filtered = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];
          if (value !== null && value !== 0 && value !== '' && value !== undefined) {
            const filteredValue = this.filterNonEmptyValues(value);
            if (filteredValue !== null) {
              filtered[key] = filteredValue;
            }
          }
        }
      }
      return Object.keys(filtered).length > 0 ? filtered : null;
    }
    
    return obj;
  }

  // Function to extract the OLD_NEW_VALUES block
  extractOldNewValues(rawString: string): string {
    const start = rawString.indexOf('OLD_NEW_VALUES =>');
    const end = rawString.length; // Assuming it's at the end of the string

    // Extract the substring containing the OLD_NEW_VALUES section
    return rawString.substring(start, end)
      .replace('OLD_NEW_VALUES =>', '')
      .trim();
  }

  // Function to extract individual objects (Old or New) by key
  extractObject(key: string, data: string): string {
    const regex = new RegExp(`"${key}":\\s*({[^]*?})`, 'm'); // Regex to capture the object
    const match = data.match(regex);

    return match ? match[1] : '{}'; // Return the matched object or empty if not found
  }

  // Safe JSON parsing with fallback
  safeJsonParse(jsonString: string): any {
    try {
      // Replace any issues like single quotes or trailing commas if needed
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Failed to parse JSON:', error);
      return {}; // Fallback to an empty object if parsing fails
    }
  }

  getChangesSummary(logData: string): string {
    const changes = this.getChangesData(logData);
    if (typeof changes === 'string') {
      return changes; // Error or no changes
    }
    
    if (changes.count <= 2) {
      return changes.paths.join(', ');
    } else {
      return `${changes.count} sprememb (kliknite za prikaz)`;
    }
  }

  getChangesData(logData: string): { count: number, paths: string[] } | string {
    try {
      if (!logData || !logData.includes('OLD_NEW_VALUES =>')) {
        return 'Ni podatkov o spremembah';
      }

      const oldNewValuesString = this.extractOldNewValues(logData);
      
      // Parse the entire OLD_NEW_VALUES as JSON
      const oldNewJson = JSON.parse(oldNewValuesString);
      const oldValues = oldNewJson.Old;
      const newValues = oldNewJson.New;

      const diffpatcher = jsondiffpatch.create({
        propertyFilter: (name, context) => {
          if (name === 'EntryDate' || name === 'Version' || name === 'Institution' || name === 'LastChangeDate' || name === 'SerializationObjectFormLink' || name === 'ClientId') {
            return false;
          }
          return true;
        }
      });

      const differences = diffpatcher.diff(oldValues, newValues);
      
      if (!differences) {
        return 'Brez sprememb';
      }

      const changedPaths = this.extractChangedPaths(differences);
      if (changedPaths.length === 0) {
        return 'Brez sprememb';
      }

      return {
        count: changedPaths.length,
        paths: changedPaths
      };

    } catch (error) {
      console.log(error);
      return 'Napaka pri branju sprememb';
    }
  }

  extractChangedPaths(diff: any, basePath: string = ''): string[] {
    const paths: string[] = [];
    
    if (!diff || typeof diff !== 'object') {
      return paths;
    }

    for (const key in diff) {
      if (diff.hasOwnProperty(key)) {
        const currentPath = basePath ? `${basePath}.${key}` : key;
        const value = diff[key];

        if (Array.isArray(value)) {
          // jsondiffpatch format: [oldValue, newValue] for changes
          // or [newValue] for additions, or [oldValue, 0, 0] for deletions
          if (value.length === 2 && value[1] !== 0) {
            paths.push(currentPath);
          } else if (value.length === 1) {
            paths.push(currentPath);
          } else if (value.length === 3 && value[2] === 0) {
            paths.push(currentPath);
          }
        } else if (typeof value === 'object' && value !== null) {
          // Nested object changes - recurse deeper
          const nestedPaths = this.extractChangedPaths(value, currentPath);
          paths.push(...nestedPaths);
        }
      }
    }

    return paths;
  }

  toggleDropdown(id: number): void {
    if (this.openDropdowns.has(id)) {
      this.openDropdowns.delete(id);
    } else {
      this.openDropdowns.add(id);
    }
  }

  isDropdownOpen(id: number): boolean {
    return this.openDropdowns.has(id);
  }

  isString(value: any): boolean {
    return typeof value === 'string';
  }

  hasChanges(logData: string): boolean {
    const changes = this.getChangesData(logData);
    return typeof changes !== 'string' && changes.count > 0;
  }

  onRowClick(read: any): void {
    if (this.hasChanges(read.LogData)) {
      this.setSelected(read);
    }
  }

  // Pagination methods
  get paginatedReads() {
    const startIndex = (this.readsCurrentPage - 1) * this.pageSize;
    return this.reads.slice(startIndex, startIndex + this.pageSize);
  }

  get paginatedUpdates() {
    const filteredUpdates = this.updates.filter(update => this.hasChanges(update.LogData));
    const startIndex = (this.updatesCurrentPage - 1) * this.pageSize;
    return filteredUpdates.slice(startIndex, startIndex + this.pageSize);
  }

  get paginatedCustomActions() {
    const startIndex = (this.customActionsCurrentPage - 1) * this.pageSize;
    return this.customactions.slice(startIndex, startIndex + this.pageSize);
  }

  get readsTotalPages() {
    return Math.ceil(this.reads.length / this.pageSize);
  }

  get updatesTotalPages() {
    const filteredUpdates = this.updates.filter(update => this.hasChanges(update.LogData));
    return Math.ceil(filteredUpdates.length / this.pageSize);
  }

  get customActionsTotalPages() {
    return Math.ceil(this.customactions.length / this.pageSize);
  }

  goToReadsPage(page: number) {
    this.readsCurrentPage = Math.max(1, Math.min(page, this.readsTotalPages));
  }

  goToUpdatesPage(page: number) {
    this.updatesCurrentPage = Math.max(1, Math.min(page, this.updatesTotalPages));
  }

  goToCustomActionsPage(page: number) {
    this.customActionsCurrentPage = Math.max(1, Math.min(page, this.customActionsTotalPages));
  }

  get filteredUpdatesCount() {
    return this.updates.filter(update => this.hasChanges(update.LogData)).length;
  }

  get filteredCustomActionsCount() {
    return this.customactions.length;
  }

  getAuditLogRecords(logtype: string) {
    this.restClient.getDataGET("api/auditlog/retrieve/" + this.currentId+"/" + logtype).subscribe((data: any) => {

      var jsonData = JSON.parse(data);

      if (logtype == "update") {
        this.updates = jsonData;
      }else if(logtype == "customaction"){
        this.customactions = jsonData;      
      } else if (logtype == "read"){
        this.reads = jsonData;
      } else if (logtype == "insert"){
        this.inserts = jsonData;
      }

    },
      error => {

      });
  }
}
